import Base from '@components/layouts/base';
import Cookieconsent from '@components/partials/common/cookieConsent';
import Footer from '@components/partials/common/footer';
import Header from '@components/partials/common/header';
import HybridClient from '@components/partials/performer/hybridClient';
import MainContentHeader from '@components/partials/performer/mainContentHeader';
import PerformerContent from '@components/partials/performer/performerContent';
import PerformerGallery from '@components/partials/performer/performerGallery';
import PerformerVideos from '@components/partials/performer/perfromerVideos';
import SimilarModelGrid from '@components/partials/performer/similarModelGrid';
import { getPerformerApi } from '@data/performerApi';
import { getVideoData } from '@data/performerVideoApi';
import styles from '@styles/scss/performer/_performerPage.module.scss';
import { FooterText } from 'lib/types/item/footer';
import { FooterLinks } from 'lib/types/item/footerLinks';
import { ImageGallery } from 'lib/types/item/imageGallery';
import { Meta } from 'lib/types/item/meta';
import { NavbarItem } from 'lib/types/item/navBarItem';
import { OfflinePerformer } from 'lib/types/item/offlinePerformer';
import { Performer } from 'lib/types/item/performer';
import { PerformerCard } from 'lib/types/item/performerCard';
import { PerformerPage } from 'lib/types/item/performerPage';
import { SidebarItem } from 'lib/types/item/sideBarItem';
import { VideoGallery } from 'lib/types/item/videoGallery';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useState } from 'react';
import PopupManager from '@components/partials/list/popupManager';
import { Popup } from 'lib/types/item/popup';

export default function Index({
    performer,
    performerPage,
    performerCards,
    navBarItems,
    sideBarItems,
    footer,
    footerLinks,
    meta,
    gender,
    offlinePerformer,
    videos,
    videoGallery,
    imageGallery,
    language,
    popupText
}: {
    performer: Performer;
    performerPage: PerformerPage;
    performerCards: PerformerCard[];
    navBarItems: NavbarItem[];
    sideBarItems: SidebarItem[];
    footer: FooterText;
    footerLinks: FooterLinks;
    meta: Meta;
    gender: string;
    offlinePerformer: OfflinePerformer;
    videos: any;
    videoGallery: VideoGallery;
    imageGallery: ImageGallery;
    language: string;
    popupText: Popup;
}): JSX.Element {
    const router = useRouter();

    if (router.isFallback) {
        return <div>Loading...</div>;
    }

    const [blurStatus, setBlurStatus] = useState('');

    /* this function sets the blur value and blurs the page when lightbox is open */
    const getBlurStatus = function (blur: string) {
        setBlurStatus(blur);
    };

    return (
        <>
            <Base currentLang={language} performer={performer.username}>
                <Head>
                    <title>{meta.meta_title}</title>
                    <meta name="description" content={meta.meta_description} />
                    <meta property="og:type" content="website" />
                    <meta property="og:site_name" content={meta.meta_title} />
                    <meta property="og:url" content={meta.canonical} />
                    <meta
                        property="og:description"
                        content={meta.meta_description}
                        key="description"
                    />
                    <meta property="og:title" name="title" content={meta.meta_title} />
                    <meta name="Rating" content="mature" />
                    <meta name="RATING" content="RTA-5042-1996-1400-1577-RTA" />
                    <meta name="theme-color" content="#FAFAFA" />
                    <link rel="shortcut icon" href="https://assets.camrabbit.sex/favicon-cr.png" />
                    <meta name="robots" content={meta.robots} />
                    <link
                        rel="canonical"
                        href={
                            language === 'en'
                                ? meta.canonical
                                : `${meta.canonical.replace('.sex/', `.sex/${language}/`)}`
                        }
                    />
                    <link rel="alternate" href={meta.canonical} hrefLang="x-default" />
                    {meta.alternate.map((alt) => (
                        <link
                            key={alt}
                            rel="alternate"
                            hrefLang={alt.replace('/', '')}
                            href={`${meta.canonical.replace(
                                '.sex/',
                                `.sex/${alt === 'en/' ? '' : alt}`
                            )}`}
                        />
                    ))}
                </Head>
                <div className={blurStatus != '' ? styles.blur : styles.noBlur}></div>
                <Header
                    sideBarItems={sideBarItems}
                    navBarItems={navBarItems}
                    currentLang={language}
                    gender={gender}
                />
                <div className={styles.performerContainer}>
                    <MainContentHeader performerPage={performerPage} performer={performer} />
                    <HybridClient
                        performer={performer}
                        gender={gender}
                        offlinePerformer={offlinePerformer}
                        currentLang={language}
                    />
                    <PerformerContent
                        performer={performer}
                        performerPage={performerPage}
                        currentLang={language}
                        gender={gender}
                    />
                    <PerformerVideos
                        performer={performer}
                        gender={gender}
                        videos={videos}
                        sendData={getBlurStatus}
                        videoGallery={videoGallery}
                        currentLang={language}
                        blurStatus={blurStatus}
                    />
                    <PerformerGallery
                        performer={performer}
                        sendData={getBlurStatus}
                        imageGallery={imageGallery}
                        blurStatus={blurStatus}
                        gender={gender}
                        currentLang={language}
                    />
                    <SimilarModelGrid
                        performerCards={performerCards}
                        performerPage={performerPage}
                        currentLang={language}
                        gender={gender}
                    />
                    <PopupManager gender={gender} currentLang={language} performer={performer} popupText={popupText}/>
                </div>
                <Footer
                    footerText={footer}
                    footerLinks={footerLinks}
                    currentLang={language}
                    gender={gender}
                />
                <Cookieconsent currentLang={language} />
            </Base>
        </>
    );
}

export const getStaticPaths: GetStaticPaths = async () => {
    const paths = [{ params: { username: 'BrielePerez' } }];
    return {
        paths,
        fallback: 'blocking'
    };
};

export const getStaticProps: GetStaticProps = async (context: any) => {
    const username = context.params.username;
    const language = context.locale;

    const performerApi = await getPerformerApi(username, language);
    const performerVideoApi = await getVideoData(username);
    

    if (!performerApi) {
        return {
            notFound: true
        };
    }

    return {
        props: {
            performer: performerApi.Performer,
            performerPage: performerApi.performerPage,
            meta: performerApi.Performer.meta,
            performerCards: performerApi.performerCards,
            footer: performerApi.footer,
            footerLinks: performerApi.footerLinks,
            navBarItems: performerApi.navbarItems,
            sideBarItems: performerApi.sidebarItems,
            gender: performerApi.gender,
            offlinePerformer: performerApi.offlinePerformer,
            videos: performerVideoApi.videos,
            videoGallery: performerApi.videoGallery,
            imageGallery: performerApi.imageGallery,
            language: language,
            popupText: performerApi.popup,
        },
        revalidate: 1
    };
};
